<template>
    <div class="input-container">
        <div class="input-wrapper" :class="[{focus: isFocused}, {'input-error': errorMessage}, {'info-text': infoMessage} , {required}]">
            <label v-if="label" for="name" :class="[{'float-label': isFocused || value}, design]">
                <span class="show-for-medium">{{ label }}</span><span class="show-for-small-only">{{ labelMobile }}</span><span v-if="required">*</span>
            </label>
            <input
                type="text"
                :name="name"
                v-model="inputValue"
                @blur="setBlur"
                @focus="setFocus"
                :class="design"
                :required="required"
                inputmode="decimal"
                pattern="[0-9]*"
                autocomplete="off"
            />
            <svg v-if="errorMessage.length" viewBox="0 0 24 24" class="error-icon">
                <path d="M11.983,0a12.206,12.206,0,0,0-8.51,3.653A11.8,11.8,0,0,0,0,12.207,11.779,11.779,0,0,0,11.8,24h.214A12.111,12.111,0,0,0,24,11.791h0A11.766,11.766,0,0,0,11.983,0ZM10.5,16.542a1.476,1.476,0,0,1,1.449-1.53h.027a1.527,1.527,0,0,1,1.523,1.47,1.475,1.475,0,0,1-1.449,1.53h-.027A1.529,1.529,0,0,1,10.5,16.542ZM11,12.5v-6a1,1,0,0,1,2,0v6a1,1,0,1,1-2,0Z"/>
            </svg>
            <p v-if="errorMessage.length" :class="['error-message', design]">{{ errorMessage }}</p>
            <p v-if="infoMessage.length" :class="['info-message', design]">{{ infoMessage }}</p>
        </div>
	</div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Emit, Model } from 'vue-property-decorator';
import { currencyToNumber, numberToCurrency } from '@/utils/utilities';
@Component
export default class CurrencyInput extends Vue
{
    @Model('input', {type: String})         readonly value!: string;
    @Prop(String)                           readonly label!: string;
    @Prop(String)                           readonly labelMobile!: string;
    @Prop(String)                           readonly name!: string;
    @Prop(String)                           readonly placeholder!: string;
    @Prop({type: String, default: ''})      readonly errorMessage!: string;
    @Prop({type: String, default: ''})      readonly infoMessage!: string;
    @Prop({type: Boolean, default: false})  readonly required!: boolean;
    @Prop({type: String, default: 'boxed'}) readonly design!: string;

    isFocused: boolean = false;

    @Emit('focus')
    public setFocus() {
        this.isFocused = true;
    }

    @Emit('blur')
    public setBlur() {
        this.isFocused = false;
    }

    get inputValue() {
        return numberToCurrency(parseFloat(this.value));
    }

    set inputValue(value: string) {
        if (!value) {
            this.$emit('input', 0);
        }
        let formattedValue = currencyToNumber('en-US', 'USD', value);
        if (isNaN(formattedValue) || Math.sign(formattedValue) == -1) {
            return;
        }
        this.$emit('input', formattedValue);
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.input-container {
    font-family: $body-font-family;
    color: $body-font-color;
    font-size: $body-font-size;
}

label {
    margin-bottom: 10px;
    position: absolute;
    top: 20px;
    left: 13px;
    transition: top .3s ease, font-size .3s ease;
    padding: 0 4px;
    pointer-events: none;
    font-size: 1rem;
    line-height: 1rem;
    color: var(--battleship-gray);
    will-change: top, font-size;

    &.underline {
        left: 0;
    }
}

input {
    height: 56px;
}

textarea {
    min-width: 100%;
    min-height: 58px;
}

input, textarea {
    border-radius: var(--global-radius);
    font-weight: 500;
    color: var(--dark-gray);
    padding: 16px 8px 0px 16px;
    box-shadow: none;
    margin-bottom: 22px; //keep space for error message
    border: 1px solid var(--medium-gray);

    &.underline {
        padding: 16px 0px 0px 4px;
        border: 0;
        border-radius: 0;
        border-bottom: 1px solid var(--medium-gray);
    }

    &::placeholder {
        color: var(--gray);
        font-weight: 400;
    }


    &:hover {
        cursor: pointer;
        background-color: var(--almost-white);
    }
}

textarea {
    &.underline {
        padding-top: 25px;
    }
}

.focus {
    input, textarea {
        border: 2px solid var(--primary);
        box-shadow: none;
        padding-left: 15px; // prevent shifts of the content due to change of border width

        &.underline {
            padding-top: 17px;
            padding-left: 4px;
            border: 0;
            border-bottom: 2px solid var(--primary);
        }
    }

    textarea {
        padding-top: 15px;
        padding-right: 7px;
        margin-bottom: 21px;

        &.underline {
            padding-top: 25px;
        }
    }

    & .float-label {
        color: var(--gray);
        &.underline {
            color: var(--primary);
        }
    }
}

.float-label {
    top: 11px;
    color: var(--battleship-gray);
    font-size: .75rem;
    transition: top .2s ease, font-size .2s ease;

    &.underline {
        color: var(--primary);
    }
}

.input-error {
    label.float-label {
        color: var(--alert);
    }
    input, textarea {
        border: 2px solid var(--alert);
        padding-left: 15px; // prevent shifts of the content due to change of border width
        margin-bottom: 0;

        &.underline {
            padding-top: 17px;
            padding-left: 4px;
            border: 0;
            border-bottom: 2px solid var(--alert);
        }
    }
    margin-bottom: 10px;
}

.info-text {
    input, textarea {
        margin-bottom: 0;
    }
    margin-bottom: 10px;
}

.error-message {
    font-size: .7rem;
    margin-left: 12px;
    margin-top: 5px;
    color: var(--alert);
    margin-bottom: 0;

    &.underline {
        margin-left: 4px;
    }
}

.info-message {
    font-size: .7rem;
    margin-left: 12px;
    margin-top: 5px;
    color: var(--battleship-gray);
    margin-bottom: 0;
}

.input-wrapper {
    position: relative;
}

.error-icon {
    position: absolute;
    top: 16px;
    right: 10px;
    width: 24px;

    path {
        fill: var(--alert);
    }
}



// remove yellow background on autofill
input:-webkit-autofill {
    transition-delay: 9999s;
    transition-property: background-color, color;
    &:hover,
    &:focus,
    &:active {
        transition-delay: 9999s;
        transition-property: background-color, color;
    }
}

input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

</style>